import React, { useState, useEffect, useRef } from "react";
import { Button } from "react-bootstrap";
import styled from "styled-components";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import moment from "moment";

import { getDailyRevenueReport } from "../../../apis/customTemplate";
import { toastify } from "../../../helperFunctions/toastify";
import DateInput from "../../SharedComponents/DateInput";
import { setTableSearch } from "../../../redux/slices/commonSlice";

import {
  customDateSort,
  downloadCsv,
  formatDate,
} from "../../../helperFunctions/utils";

const FilterWrapper = styled.div`
  label {
    color: inherit;
  }

  .btn-wrapper {
    align-items: center;
    display: flex;
    margin-top: 20px;
  }
`;

function RevenueReportView() {
  const numberFormatter = Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const buttonRef = useRef(null);
  const pathname = location.pathname;
  const fieldPathname = pathname.replace("/", "");

  const { tableSearch } = useSelector((state) => state.commonData);

  const [filteredData, setFilteredData] = useState([]);
  const [revenueList, setRevenueList] = useState([]);
  const [startDate, setStartDate] = useState(
    tableSearch[fieldPathname]?.refundL?.startDate
      ? tableSearch[fieldPathname]?.refundL?.startDate
      : `${new Date().getFullYear()}-01-01`
  );
  const [endDate, setEndDate] = useState(
    tableSearch[fieldPathname]?.refundL?.endDate
      ? tableSearch[fieldPathname]?.refundL?.endDate
      : `${new Date().getFullYear()}-12-29`
  );
  const [refetch, setRefetch] = useState(false);

  const onReduxFilter = (valueStartDate, valueEndDate) => {
    dispatch(
      setTableSearch({
        ...tableSearch,
        [fieldPathname]: {
          ...tableSearch[fieldPathname],
          refundL: {
            startDate: valueStartDate,
            endDate: valueEndDate,
          },
        },
      })
    );
  };

  useEffect(() => {
    setStartDate(
      tableSearch[fieldPathname]?.refundL?.startDate
        ? tableSearch[fieldPathname]?.refundL?.startDate
        : `${new Date().getFullYear()}-01-01`
    );
    setEndDate(
      tableSearch[fieldPathname]?.refundL?.endDate
        ? tableSearch[fieldPathname]?.refundL?.endDate
        : `${new Date().getFullYear()}-12-29`
    );

    if (
      buttonRef.current &&
      (tableSearch[fieldPathname]?.refundL?.startDate ||
        tableSearch[fieldPathname]?.refundL?.endDate)
    ) {
      setTimeout(() => {
        if (buttonRef.current) {
          buttonRef.current.click();
        }
      }, [1000]);
    }
  }, []);

  useEffect(() => {
    getDailyRevenueReport({
      start_date: startDate,
      end_date: endDate,
    }).then((res) => {
      console.log("res?.result?.data", res?.result?.data);
      if (res?.result?.data) {
        setRevenueList(res.result.data);
        setFilteredData(res.result.data);
      }
    });
  }, [refetch]);

  const formatValue = (value) => {
    const num = Number(value || 0);
    return num % 1 === 0 ? num.toString() : num.toFixed(2);
  };

  const getMonthName = (monthNumber) => {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return monthNames[monthNumber - 1] || "Invalid Month";
  };

  const columns = [
    {
      name: "Date",
      selector: (row) => row.date || 0,
      cell: (row) => row.date,
    },
    {
      name: "Patient",
      selector: (row) => row.patient || 0,
      cell: (row) => row.patient,
    },
    {
      name: "Total",
      selector: (row) => row.amount,
      cell: (row) => numberFormatter.format(row.amount),
    },
  ];

  const data = filteredData?.map((e, index) => {
    return {
      ...e,
      index,
      key: index,
    };
  });

  const customStyles = {
    headCells: {
      style: {
        justifyContent: "center",
      },
    },
    cells: {
      style: {
        justifyContent: "center",
      },
    },
  };

  const conditionalRowStyles = [
    {
      when: (row) => row,
      style: {
        borderWidth: "0px",
      },
    },
    {
      when: (row) => row.index % 2 === 1,
      style: {
        backgroundColor: "#f8f8f8",
        borderWidth: "0px",
      },
    },
  ];

  const exportCsv = () => {
    const csvData = revenueList.map((row) => {
      return {
        Date: row.date || "",
        Patient: row.patient,
        Amount: numberFormatter.format(row.amount),
      };
    });

    downloadCsv(csvData, "Revenue_Report");
  };

  return (
    <div className="p-0">
      <div
        className="mainHeading"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            marginTop: "18px",
          }}
        >
          <FilterWrapper className="d-flex">
            <div className="me-3" style={{ width: "260px" }}>
              <DateInput
                value={startDate}
                label="Start Date"
                onChange={(e) => {
                  setStartDate(e);
                }}
              />
            </div>
            <div className="me-3" style={{ width: "260px" }}>
              <DateInput
                value={endDate}
                label="End Date"
                onChange={(e) => {
                  setEndDate(e);
                }}
              />
            </div>
            <div className="btn-wrapper">
              <Button
                ref={buttonRef}
                className="addItem me-3"
                onClick={() => {
                  if (endDate < startDate) {
                    toastify("End date is less than start date", "error");
                    return;
                  }
                  onReduxFilter(startDate, endDate);
                  setRefetch(!refetch);
                }}
              >
                Search
              </Button>
              <Button className="addItem" onClick={() => exportCsv()}>
                Export
              </Button>
            </div>
          </FilterWrapper>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={data || []}
        pagination
        customStyles={customStyles}
        conditionalRowStyles={conditionalRowStyles}
      />
    </div>
  );
}

export default RevenueReportView;
